import React, { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TableRowsSkeleton } from "@/components/LoadingSkeleton";
import {
  TrendingUp, Search, RotateCcw, ChevronDown, ChevronRight,
  Calendar as CalendarIcon, ExternalLink, Filter as FilterIcon,
  Trash2, User as UserIcon,
} from "lucide-react";

/**
 * Iter 42.5 — Productivity Report (redesigned).
 *
 * Replaces the old status-transition based report. Consumes the new
 * `/api/productivity/marks` + `/api/productivity/summary` endpoints. See
 * productivity_marks_routes.py for the data contract.
 */

const PROFILES = [
  { value: "",             label: "All profiles" },
  { value: "human_writer", label: "Human Writer" },
  { value: "ai_writer",    label: "AI Writer" },
  { value: "editor",       label: "Editor" },
  { value: "publisher",    label: "Publisher" },
];

const DEPTS = [
  { value: "",          label: "All departments" },
  { value: "content",   label: "Content" },
  { value: "seo",       label: "SEO" },
  { value: "marketing", label: "Marketing" },
  { value: "hr",        label: "HR" },
];

const QUICK_RANGES = [
  { value: "today",     label: "Today" },
  { value: "week",      label: "This Week" },
  { value: "month",     label: "This Month" },
  { value: "specific",  label: "Specific Date" },
  { value: "custom",    label: "Date Range" },
  { value: "all",       label: "All time" },
];

const PROFILE_STYLE = {
  human_writer: "bg-sky-50 text-sky-700 border-sky-200",
  ai_writer:    "bg-purple-50 text-purple-700 border-purple-200",
  editor:       "bg-amber-50 text-amber-700 border-amber-200",
  publisher:    "bg-emerald-50 text-emerald-700 border-emerald-200",
};

function fmtDateOnly(iso) {
  if (!iso) return "—";
  // iso here is already YYYY-MM-DD from the server
  try {
    const d = new Date(iso + "T00:00:00");
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso; }
}

// Quick-range → { date_from, date_to } (YYYY-MM-DD)
function rangeDates(range, specific, custom_from, custom_to) {
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const startOfWeek = (d) => { const x = new Date(d); const dow = x.getDay(); const diff = dow === 0 ? -6 : 1 - dow; x.setDate(x.getDate() + diff); return x; };
  switch (range) {
    case "today":    return { date_from: iso(today), date_to: iso(today) };
    case "week":     return { date_from: iso(startOfWeek(today)), date_to: iso(today) };
    case "month":    { const s = new Date(today.getFullYear(), today.getMonth(), 1); return { date_from: iso(s), date_to: iso(today) }; }
    case "specific": return specific ? { date_from: specific, date_to: specific } : {};
    case "custom":   return { date_from: custom_from || "", date_to: custom_to || "" };
    case "all":
    default:         return {};
  }
}

function ProfileChip({ profile }) {
  const label = {
    human_writer: "Human Writer",
    ai_writer:    "AI Writer",
    editor:       "Editor",
    publisher:    "Publisher",
  }[profile] || profile || "—";
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-medium ${PROFILE_STYLE[profile] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
      {label}
    </span>
  );
}

function StatCard({ label, value, sub, tone = "slate", testid }) {
  const toneCls = {
    slate:   "border-slate-200 bg-white text-slate-800",
    emerald: "border-emerald-200 bg-emerald-50/40 text-emerald-800",
    sky:     "border-sky-200 bg-sky-50/40 text-sky-800",
    amber:   "border-amber-200 bg-amber-50/40 text-amber-800",
    purple:  "border-purple-200 bg-purple-50/40 text-purple-800",
  }[tone];
  return (
    <div className={`rounded-lg border p-3 ${toneCls}`} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-2xl font-bold mt-0.5">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function MarkDetail({ mark, onDelete, canDelete }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid={`mark-detail-${mark.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <ProfileChip profile={mark.profile} />
          <span className="text-sm font-medium text-slate-800">
            {mark.count} {mark.profile === "human_writer" ? "words" : mark.profile === "publisher" ? "published" : mark.profile === "editor" ? "edited" : "content"}
          </span>
        </div>
        {canDelete && (
          <button
            onClick={() => { if (confirm("Delete this productivity mark?")) onDelete(mark.id); }}
            className="text-slate-400 hover:text-rose-600 p-1"
            data-testid={`mark-delete-${mark.id}`}
            title="Delete"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <div className="text-[11px] text-slate-500 mb-1">
        Project: <span className="text-slate-700 font-medium">{mark.project_name || "—"}</span>
      </div>
      {(mark.links || []).length > 0 && (
        <div className="space-y-1 mt-2">
          <div className="text-[10px] font-semibold text-slate-500 uppercase">Links</div>
          {(mark.links || []).map((l, i) => (
            <a
              key={i}
              href={l}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 text-xs text-purple-600 hover:underline truncate"
              data-testid={`mark-link-open-${mark.id}-${i}`}
            >
              <ExternalLink className="h-3 w-3 shrink-0" /> {l}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProductivityReport() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const initialProjectFilter = params.get("project_id") || "";

  const [directory, setDirectory] = useState([]);
  const [selectedUser, setSelectedUser] = useState(""); // user_id
  const [profile, setProfile] = useState("");
  const [department, setDepartment] = useState("");
  // Iter 42.5.1 — default to "This Week" for a useful landing state
  // (the previous "This Month" default hid all-time data when no marks
  // existed in the current calendar month, making the report look broken).
  const [range, setRange] = useState("week");
  const [specific, setSpecific] = useState("");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  // Threshold filters — profile-scoped. Empty string = no filter.
  const [wcMin, setWcMin] = useState(""); const [wcMax, setWcMax] = useState("");
  const [ccMin, setCcMin] = useState(""); const [ccMax, setCcMax] = useState("");
  const [pcMin, setPcMin] = useState(""); const [pcMax, setPcMax] = useState("");
  const [projectFilter] = useState(initialProjectFilter);

  const [marks, setMarks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set()); // mark ids expanded
  const [userSummary, setUserSummary] = useState(null);      // { cards: [...] }
  const [summaryLoading, setSummaryLoading] = useState(false);
  // Iter 42.5.1 — team-level rollup shown when no specific user is picked.
  const [leaderboard, setLeaderboard] = useState(null);       // { leaders: [...] }
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);

  const isAdminOrDA = ["admin", "super_admin", "department_admin"].includes(user?.role);
  const isCM = user?.role === "content_manager";
  const isTL = user?.role === "team_lead";
  const isSelfOnly = !isAdminOrDA && !isCM && !isTL;

  // Load directory once
  useEffect(() => {
    api.get("/productivity/directory")
      .then(({ data }) => setDirectory(Array.isArray(data) ? data : []))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  // Auto-scope to self for producers who can't view others.
  useEffect(() => {
    if (isSelfOnly && user?.id) setSelectedUser(user.id);
  }, [isSelfOnly, user?.id]);

  const buildQuery = useCallback(() => {
    const { date_from, date_to } = rangeDates(range, specific, customFrom, customTo);
    const q = new URLSearchParams();
    if (selectedUser) q.set("user_id", selectedUser);
    if (profile)      q.set("profile", profile);
    if (department)   q.set("department", department);
    if (projectFilter) q.set("project_id", projectFilter);
    if (date_from)    q.set("date_from", date_from);
    if (date_to)      q.set("date_to", date_to);
    if (wcMin)        q.set("word_count_min", wcMin);
    if (wcMax)        q.set("word_count_max", wcMax);
    if (ccMin)        q.set("content_count_min", ccMin);
    if (ccMax)        q.set("content_count_max", ccMax);
    if (pcMin)        q.set("published_count_min", pcMin);
    if (pcMax)        q.set("published_count_max", pcMax);
    return q.toString();
  }, [selectedUser, profile, department, projectFilter, range, specific, customFrom, customTo, wcMin, wcMax, ccMin, ccMax, pcMin, pcMax]);

  const loadMarks = useCallback(async () => {
    setLoading(true);
    try {
      const qs = buildQuery();
      const { data } = await api.get(`/productivity/marks${qs ? "?" + qs : ""}`);
      setMarks(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [buildQuery]);

  useEffect(() => { loadMarks(); }, [loadMarks]);

  // Load per-user summary cards (only when a single user is picked).
  useEffect(() => {
    if (!selectedUser) { setUserSummary(null); return; }
    setSummaryLoading(true);
    api.get(`/productivity/summary?user_id=${selectedUser}`)
      .then(({ data }) => setUserSummary(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setSummaryLoading(false));
  }, [selectedUser]);

  // Iter 42.5.1 — team-level leaderboard when NO specific user is picked.
  // Rebuilds whenever the shared filters (range/department/profile) change.
  useEffect(() => {
    if (selectedUser) { setLeaderboard(null); return; }
    setLeaderboardLoading(true);
    const { date_from, date_to } = rangeDates(range, specific, customFrom, customTo);
    const q = new URLSearchParams();
    if (date_from)  q.set("date_from", date_from);
    if (date_to)    q.set("date_to", date_to);
    if (department) q.set("department", department);
    if (profile)    q.set("profile", profile);
    q.set("top", "10");
    api.get(`/productivity/leaderboard?${q.toString()}`)
      .then(({ data }) => setLeaderboard(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLeaderboardLoading(false));
  }, [selectedUser, range, specific, customFrom, customTo, department, profile]);

  const resetFilters = () => {
    setSelectedUser(isSelfOnly ? user?.id || "" : "");
    setProfile(""); setDepartment("");
    setRange("week"); setSpecific(""); setCustomFrom(""); setCustomTo("");
    setWcMin(""); setWcMax(""); setCcMin(""); setCcMax(""); setPcMin(""); setPcMax("");
  };

  const toggleExpand = (id) => setExpanded((p) => {
    const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });

  const onDelete = async (id) => {
    try {
      await api.delete(`/productivity/marks/${id}`);
      toast.success("Mark deleted");
      setMarks((prev) => prev.filter((m) => m.id !== id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const selectedUserDoc = useMemo(
    () => directory.find((u) => u.id === selectedUser) || null,
    [directory, selectedUser],
  );

  return (
    <div className="p-6 space-y-4 min-w-[1200px]" data-testid="productivity-report-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-emerald-600" /> Productivity Report
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Self-reported output by writers, editors and publishers.
            {isSelfOnly && " Showing only your own marks."}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={resetFilters} data-testid="reset-filters-btn">
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset filters
        </Button>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="productivity-filters">
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase mb-3">
          <FilterIcon className="h-3.5 w-3.5" /> Filters
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <div>
            <label className="text-[11px] text-slate-600">Member</label>
            <Select value={selectedUser || "__all"} onValueChange={(v) => setSelectedUser(v === "__all" ? "" : v)} disabled={isSelfOnly}>
              <SelectTrigger className="h-9 mt-1" data-testid="filter-member"><SelectValue placeholder="All members" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All members</SelectItem>
                {directory.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] text-slate-600">Department</label>
            <Select value={department || "__all"} onValueChange={(v) => setDepartment(v === "__all" ? "" : v)}>
              <SelectTrigger className="h-9 mt-1" data-testid="filter-department"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DEPTS.map((d) => (
                  <SelectItem key={d.value || "__all"} value={d.value || "__all"}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] text-slate-600">Profile</label>
            <Select value={profile || "__all"} onValueChange={(v) => setProfile(v === "__all" ? "" : v)}>
              <SelectTrigger className="h-9 mt-1" data-testid="filter-profile"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PROFILES.map((p) => (
                  <SelectItem key={p.value || "__all"} value={p.value || "__all"}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] text-slate-600">Quick Range</label>
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger className="h-9 mt-1" data-testid="filter-range"><SelectValue /></SelectTrigger>
              <SelectContent>
                {QUICK_RANGES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {range === "specific" && (
            <div>
              <label className="text-[11px] text-slate-600">Date</label>
              <Input type="date" value={specific} onChange={(e) => setSpecific(e.target.value)} className="h-9 mt-1" data-testid="filter-specific-date" />
            </div>
          )}
          {range === "custom" && (
            <>
              <div>
                <label className="text-[11px] text-slate-600">From</label>
                <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-9 mt-1" data-testid="filter-custom-from" />
              </div>
              <div>
                <label className="text-[11px] text-slate-600">To</label>
                <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-9 mt-1" data-testid="filter-custom-to" />
              </div>
            </>
          )}
        </div>

        {/* Threshold filters — collapsible group */}
        <details className="mt-3 group">
          <summary className="text-xs font-medium text-slate-600 cursor-pointer inline-flex items-center gap-1 hover:text-slate-800" data-testid="threshold-filters-toggle">
            <ChevronRight className="h-3.5 w-3.5 group-open:rotate-90 transition" /> Threshold filters (word / content / published)
          </summary>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
            <div>
              <label className="text-[11px] text-slate-600">Word Count (min)</label>
              <Input type="number" value={wcMin} onChange={(e) => setWcMin(e.target.value)} placeholder="more than…" className="h-9 mt-1" data-testid="filter-wc-min" />
            </div>
            <div>
              <label className="text-[11px] text-slate-600">Word Count (max)</label>
              <Input type="number" value={wcMax} onChange={(e) => setWcMax(e.target.value)} placeholder="less than…" className="h-9 mt-1" data-testid="filter-wc-max" />
            </div>
            <div />
            <div>
              <label className="text-[11px] text-slate-600">Content Generated (min)</label>
              <Input type="number" value={ccMin} onChange={(e) => setCcMin(e.target.value)} placeholder="more than…" className="h-9 mt-1" data-testid="filter-cc-min" />
            </div>
            <div>
              <label className="text-[11px] text-slate-600">Content Generated (max)</label>
              <Input type="number" value={ccMax} onChange={(e) => setCcMax(e.target.value)} placeholder="less than…" className="h-9 mt-1" data-testid="filter-cc-max" />
            </div>
            <div />
            <div>
              <label className="text-[11px] text-slate-600">Published (min)</label>
              <Input type="number" value={pcMin} onChange={(e) => setPcMin(e.target.value)} placeholder="more than…" className="h-9 mt-1" data-testid="filter-pc-min" />
            </div>
            <div>
              <label className="text-[11px] text-slate-600">Published (max)</label>
              <Input type="number" value={pcMax} onChange={(e) => setPcMax(e.target.value)} placeholder="less than…" className="h-9 mt-1" data-testid="filter-pc-max" />
            </div>
          </div>
        </details>
      </div>

      {/* Per-user KPI cards (only when a single user is picked) */}
      {selectedUser && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="user-summary-cards">
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase mb-3">
            <UserIcon className="h-3.5 w-3.5" /> {selectedUserDoc?.name || "Member"} — Live totals
          </div>
          {summaryLoading ? (
            <div className="text-sm text-slate-400">Loading…</div>
          ) : (userSummary?.cards || []).length === 0 ? (
            <div className="text-sm italic text-slate-400" data-testid="user-summary-empty">
              No productivity marks yet for this member.
            </div>
          ) : (
            <div className="space-y-4">
              {userSummary.cards.map((c) => (
                <div key={c.profile}>
                  <div className="flex items-center gap-2 mb-2">
                    <ProfileChip profile={c.profile} />
                    <span className="text-xs text-slate-500">
                      {c.profile === "publisher" ? "posts" : c.profile === "editor" ? "content edited" : c.profile === "ai_writer" ? "content created" : "words"}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard
                      label={c.profile === "human_writer" ? "Total Word Count" : c.profile === "publisher" ? "Total Published" : c.profile === "editor" ? "Total Content Edited" : "Total Content Created"}
                      value={c.total_all_time.toLocaleString()}
                      tone={c.profile === "human_writer" ? "sky" : c.profile === "publisher" ? "emerald" : c.profile === "editor" ? "amber" : "purple"}
                      testid={`card-total-${c.profile}`}
                    />
                    <StatCard
                      label="This Week"
                      value={c.this_week.toLocaleString()}
                      testid={`card-week-${c.profile}`}
                    />
                    <StatCard
                      label="Weekly Average"
                      sub="This-week total ÷ 5"
                      value={c.weekly_avg.toLocaleString()}
                      testid={`card-avg-${c.profile}`}
                    />
                    <StatCard
                      label="This Month"
                      value={c.this_month.toLocaleString()}
                      testid={`card-month-${c.profile}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Iter 42.5.1 — Team leaderboard (only when NO specific user is
          picked). Aggregates by profile using the same shared filters
          (range / department / profile). Empty profiles hide themselves.
          Clicking a row deep-selects that member into the Member filter,
          which swaps this card out for the per-user KPI card. */}
      {!selectedUser && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="team-leaderboard-card">
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase mb-3">
            <TrendingUp className="h-3.5 w-3.5" /> Team leaderboard
            <span className="text-slate-400 normal-case font-normal">
              — top performers for the selected range
            </span>
          </div>
          {leaderboardLoading ? (
            <div className="text-sm text-slate-400">Loading leaderboard…</div>
          ) : !leaderboard || (leaderboard.leaders || []).every((g) => (g.entries || []).length === 0) ? (
            <div className="text-sm italic text-slate-400" data-testid="leaderboard-empty">
              No productivity marks in this range. Try widening the date filter or ask team members to log via "Mark Productivity" in their project spreadsheets.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(leaderboard.leaders || []).filter((g) => (g.entries || []).length > 0).map((group) => (
                <div key={group.profile} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`leaderboard-group-${group.profile}`}>
                  <div className={`px-3 py-2 border-b border-slate-100 flex items-center justify-between ${PROFILE_STYLE[group.profile] || "bg-slate-50"}`}>
                    <div className="flex items-center gap-2">
                      <ProfileChip profile={group.profile} />
                      <span className="text-xs text-slate-600">by {group.unit}</span>
                    </div>
                    <span className="text-[11px] font-semibold text-slate-500">
                      {group.entries.length} {group.entries.length === 1 ? "member" : "members"}
                    </span>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {group.entries.map((row, i) => {
                      const rank = i + 1;
                      const rankCls = rank === 1 ? "bg-amber-400 text-amber-900" : rank === 2 ? "bg-slate-300 text-slate-800" : rank === 3 ? "bg-orange-300 text-orange-900" : "bg-slate-100 text-slate-500";
                      return (
                        <button
                          key={row.user_id}
                          type="button"
                          onClick={() => setSelectedUser(row.user_id)}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-50 text-left transition"
                          data-testid={`leaderboard-row-${group.profile}-${row.user_id}`}
                        >
                          <span className={`h-6 w-6 rounded-full text-[11px] font-bold flex items-center justify-center shrink-0 ${rankCls}`}>
                            {rank}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-slate-800 truncate">{row.user_name}</div>
                            <div className="text-[11px] text-slate-500 capitalize truncate">
                              {(row.user_role || "").replace("_", " ")} · {(row.departments || []).join(", ") || "—"}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-sm font-bold text-slate-800 tabular-nums">{row.total.toLocaleString()}</div>
                            <div className="text-[10px] text-slate-500">{row.mark_count} mark{row.mark_count === 1 ? "" : "s"}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Marks table */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="marks-table-card">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-800">
            Productivity Marks <span className="text-slate-400 font-normal">· {marks.length}</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                <th className="w-8 px-3 py-2"></th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Member</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2">Profile</th>
                <th className="px-3 py-2">Departments</th>
                <th className="px-3 py-2">CM</th>
                <th className="px-3 py-2">TL</th>
              </tr>
            </thead>
            <tbody>
              {loading && <TableRowsSkeleton rows={6} cols={8} />}
              {!loading && marks.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-slate-400 italic py-8" data-testid="marks-empty">
                    No productivity marks match your filters.
                  </td>
                </tr>
              )}
              {!loading && marks.map((m) => {
                const isOpen = expanded.has(m.id);
                const canDelete = m.user_id === user?.id || ["admin", "super_admin"].includes(user?.role);
                return (
                  <React.Fragment key={m.id}>
                    <tr className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`mark-row-${m.id}`}>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => toggleExpand(m.id)}
                          className="text-slate-400 hover:text-slate-800 p-0.5"
                          data-testid={`mark-toggle-${m.id}`}
                        >
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-slate-700">
                        <CalendarIcon className="h-3 w-3 inline mr-1 text-slate-400" />
                        {fmtDateOnly(m.date)}
                      </td>
                      <td className="px-3 py-2 text-slate-800 font-medium">{m.user_name}</td>
                      <td className="px-3 py-2 text-slate-600 capitalize">{(m.user_role || "").replace("_", " ")}</td>
                      <td className="px-3 py-2"><ProfileChip profile={m.profile} /></td>
                      <td className="px-3 py-2 text-slate-600 text-xs">
                        {(m.departments || []).map((d) => (
                          <span key={d} className="mr-1 inline-block bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 capitalize">{d}</span>
                        ))}
                      </td>
                      <td className="px-3 py-2 text-slate-600 text-xs">{(m.content_manager_names || []).join(", ") || "—"}</td>
                      <td className="px-3 py-2 text-slate-600 text-xs">{(m.team_lead_names || []).join(", ") || "—"}</td>
                    </tr>
                    {isOpen && (
                      <tr className="border-b border-slate-200 bg-slate-50/30">
                        <td></td>
                        <td colSpan={7} className="px-3 py-3">
                          <MarkDetail mark={m} onDelete={onDelete} canDelete={canDelete} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
